<template>
  <div>
    <b-card no-body class="mx-n3">
      <b-row>
        <b-col>
          <XtendHeatmap
            :url="urls.topDomainUrl"
            :alertUrl="urls.networkAlertsUrl"
            :timeRange="timeRange"
          />
        </b-col>
      </b-row>
      <b-row class="mx-1 mt-2 mb-1" align-h="between">
        <b-col lg="4" md="4" sm="12" class="text-left mb-1">
          <div class="form-select d-inline-block">
            Show
            <b-form-select
                  v-model="pageSize"
                  @change="handlePageSizeChanged"
                  :options="pageSizeOptions"
                  style="width: 60px;"
                  size="sm"
                ></b-form-select>
            Entries
          </div>
        </b-col>
        <b-col lg="4" md="4" sm="12" class="d-flex justify-content-center mb-1">
          <TrafficTotalCon
          :url = "urls.networkConnectionsCountUrl"
          :timeRange="timeRange"
          @filterDirection="filterDirection"
          />
        </b-col>
        <b-col lg="4" md="4" sm="12" class="d-flex justify-content-end mb-1">
          <div class="d-flex align-items-center">
            <div class="mr-2 d-flex align-items-center">
              <SearchBar :hasToolTip="true" :hasErrorMessage="true">
                <template #input>
                  <b-form-input
                    v-model="searchTerm"
                    type="search"
                    placeholder="Search domains..."
                    @input="handleSearch"
                  />
                </template>
              </SearchBar>
            </div>
            <ExportCsvButton
              dataType="traffic"
              :data="exportData"
              :usesPagination="true"
              @exportData="handleExportData"
            />
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div v-if="hasData" class="table-container">
            <b-table
              responsive
              class="table-with-actions-min-height px-2"
              striped
              outlined
              :items="currentPageData"
              :fields="tableFields"
              :per-page="perPage"
              :current-page="currentPage"
              @row-selected="onRowSelected"
              select-mode="single"
              selectable
            >
              <template #cell(count)="row">
                {{ formatNumberWithCommas(row.value) }}
              </template>
              <template #cell(organization)="row">
                <span
                  v-b-tooltip.hover
                  :title="row.value"
                >
                  {{ truncate(row.value) }}
                </span>
              </template>
              <template #cell(application)="row">
                <span
                  v-b-tooltip.hover
                  :title="row.value"
                >
                  {{ truncate(row.value) }}
                </span>
              </template>
              <template #cell(behavior)="row">
                <SecurityBehavior
                  :behavior="row.value"
                ></SecurityBehavior>
              </template>
              <template #cell(threatFeeds)="row">
                <ThreatFeedIcons
                  :threatFeedInfo="row.item.threatFeeds"
                  :threatFeedTypes="threatFeedTypes"
                  :uniqueId="row.item.ip">
                </ThreatFeedIcons>
              </template>
            </b-table>
          </div>
          <div v-else-if="loaded" class="mx-1 pl-1 mb-1">
            {{ $t("No Data Available") }}
          </div>
          <SpinnerCmpt v-else class="mb-1"></SpinnerCmpt>
        </b-col>
      </b-row>
      <b-row v-if="hasData">
        <b-col>
          <h5
            class="m-1 text-right"
          >
            <span
              class="total-addresses-wrapper"
              v-b-tooltip.hover
              :title="getTotalTrafficCountTooltip"
            >
              Approximate Count: {{ this.trafficCount }}
            </span>
          </h5>
        </b-col>
        <b-col>
          <div class="nav-btns">
            <b-button
              class="no-click align-items-center mr-1"
            >
              {{ this.documentCounterText }}
            </b-button>
            <b-button
              class="nav-btn"
              :class="{active: currentPageIndex > 0}"
              :disabled="currentPageIndex === 0"
              :style="{'background-color': toggleColor}"
              @click="previousPage"
            >
              <i class="fas fa-chevron-left"></i>
            </b-button>
            <b-button
              class="nav-btn"
              :class="{active: hasMore}"
              :disabled="!hasMore"
              :style="{'background-color': toggleColor}"
              @click="nextPage"
            >
              <i class="fas fa-chevron-right"></i>
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <DomainActivity
      v-if="selected.length > 0"
      :timeRange="timeRange"
      :url="urls.domainActivity"
    ></DomainActivity>
  </div>
</template>

<script>
import DomainActivity from '@/xvisor/components/xtend/tabs/DomainActivity.vue';
import ExportCsvButton from '@/xvisor/components/ExportCsvButton.vue';
import SearchBar from '@/xvisor/components/SearchBar.vue';
import SecurityBehavior from '@/xvisor/components/security/SecurityBehavior.vue';
import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import TrafficTotalCon from '@/xvisor/components/xtend/tabs/TrafficTotalCon.vue';
import ThreatFeedIcons from '@/xvisor/components/ThreatFeedIcons.vue';
import XomesEventBus from '@/xvisor/bus/XomesEventBus';
import XtendHeatmap from '@/xvisor/components/xtend/Heatmap.vue';
import collapseText from '@/xvisor/utilities/collapseText';
import colorPalette from '@/xvisor/constants/colorPalette';
import formatNumberWithCommas from '@/xvisor/utilities/addCommaToNumber';
import hostnameFormatter from '@/xvisor/utilities/hostnameFormatter';
import themeStyle from '@/xvisor/utilities/themeStyle';

export default {
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    urls: {
      type: Object,
      required: true,
    },
  },
  components: {
    DomainActivity,
    ExportCsvButton,
    SearchBar,
    SecurityBehavior,
    SpinnerCmpt,
    TrafficTotalCon,
    XtendHeatmap,
    ThreatFeedIcons,
  },
  data() {
    return {
      createPitUrl: '/es/pit/createid',
      currentPageIndex: 0,
      deletePitUrl: '/security/pit/delete',
      threatFeedsUrl: '/security/threatfeeds.json',
      debounceTimeout: null,
      direction: 'all',
      exportData: [],
      loaded: false,
      pageSize: 25,
      pageSizeOptions: [
        { value: 10, text: '10' },
        { value: 25, text: '25' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      pitId: null,
      searchTerm: '',
      tableFields: [
        { key: 'ip', label: 'IP', sortable: true },
        { key: 'organization', label: 'Domain', sortable: true },
        { key: 'domain', label: 'Url', sortable: true },
        { key: 'application', label: 'App', sortable: true },
        { key: 'count', label: 'Connections', sortable: true },
        { key: 'threatFeeds', label: 'Threat', sortable: false },
        { key: 'behavior', label: 'Behavior', sortable: true },
      ],
      pages: [],
      selected: [],
      url: this.urls.networkConnectionsUrl,
      trafficCountUrl: this.urls.networkConnectionsPaginationCountUrl,
      threatFeedTypes: {},
      trafficCount: 0,
      pageCount: 1,
      maxTextLength: 10,
    };
  },
  computed: {
    currentPageData() {
      if (this.pages[this.currentPageIndex]) {
        return this.pages[this.currentPageIndex].buckets;
      }
      return [];
    },
    hasData() {
      return this.loaded && this.pages.length > 0 && this.pages[0].buckets.length > 0;
    },
    hasMore() {
      const res = this.pages[this.currentPageIndex]
        && !!this.pages[this.currentPageIndex].afterKey
        && this.pages[this.currentPageIndex].buckets.length === this.pageSize;
      return res;
    },
    toggleColor() {
      return `${themeStyle.styleToggle(colorPalette.orange, colorPalette.blue)} !important`;
    },
    documentCounterText() {
      if (this.pages[this.currentPageIndex] && this.pages[this.currentPageIndex].buckets.length) {
        const start = `${this.pageCount}`;
        const end = `${this.pageCount + this.pages[this.currentPageIndex].buckets.length - 1}`;
        return `${start} - ${end}`;
      }
      return '';
    },
    getTotalTrafficCountTooltip() {
      return 'Approximate count of total traffic';
    },
  },
  methods: {
    truncate(text) {
      if (text.length > this.maxTextLength) {
        const substring = text.substring(0, this.maxTextLength);
        return `${substring}...`;
      }
      return text;
    },
    deletePitId() {
      if (this.pitId) {
        this.$http.delete(this.deletePitUrl, {
          params: {
            id: this.pitId,
          },
        });
      }
    },
    async getTrafficCount(params) {
      this.loaded = false;
      const response = await this.$http.get(this.trafficCountUrl, { params });
      const data = await response.data;
      this.trafficCount = data.total;
      this.loaded = true;
    },
    async fetchPage(params) {
      this.loaded = false;
      try {
        const UNKNOWN_DOMAIN = 'zzzzz';
        const response = await this.$http.get(this.url, { params });
        const data = await response.data;
        const newBuckets = data.data.map((bucket) => ({
          application: bucket.application,
          behavior: bucket.behavior,
          count: bucket.count,
          direction: bucket.direction,
          domain: bucket.domain === UNKNOWN_DOMAIN ? '' : bucket.domain,
          ip: bucket.direction === 'inbound' ? bucket.source : bucket.destination,
          organization: bucket.organization === '' ? '' : bucket.organization,
          threatFeeds: bucket.threatFeeds,
        }));
        return {
          buckets: newBuckets,
          afterKey: data.afterKey || null,
        };
      } catch (error) {
        return null;
      } finally {
        this.loaded = true;
      }
    },
    async fetchThreatFeeds() {
      this.loaded = false;
      const response = await this.$http.get(this.threatFeedsUrl);
      const threatFeedsData = await response.data;
      threatFeedsData.forEach((val) => { this.threatFeedTypes[val.type] = val; });
      this.loaded = true;
    },
    filterDirection(direction) {
      this.direction = direction;
      this.pages = [];
      this.currentPageIndex = 0;
      this.pageCount = 1;
      this.searchAfter = null;
      this.loadTrafficCount();
      this.loadInitialPage();
    },
    getParams(afterKey = null, size = null) {
      const params = new URLSearchParams();
      params.append('start', this.timeRange.start.toISOString());
      params.append('end', this.timeRange.end.toISOString());
      params.append('size', size ?? this.pageSize);
      if (this.direction !== 'all') {
        params.append('directionFilter', this.direction);
      }
      if (this.searchTerm !== '') {
        params.append('searchTerm', this.searchTerm);
      }
      if (afterKey) {
        const {
          app, destinationIP, direction, domain, sourceIP,
        } = afterKey;
        params.append('app', app);
        params.append('destinationIP', destinationIP);
        params.append('direction', direction);
        params.append('domain', domain);
        params.append('sourceIP', sourceIP);
      }
      if (this.pitId) {
        params.append('pit', this.pitId);
      }
      return params;
    },
    async getPitId() {
      try {
        const response = await this.$http
          .get(this.createPitUrl, {
            params: {
              indexName: 'xtend-network-connection',
              version: 8,
            },
          });
        const { id } = response.data;
        this.pitId = id;
      } catch (_err) {
        this.pitId = null;
      }
    },
    async handleExportData() {
      const MAX_AGG = 10000;
      const params = this.getParams(null, MAX_AGG);
      const result = await this.fetchPage(params);
      this.exportData = result.buckets;
    },
    handlePageSizeChanged() {
      this.pages = [];
      this.currentPageIndex = 0;
      this.pageCount = 1;
      this.searchAfter = null;
      this.selected = [];
      this.loadTrafficCount();
      this.loadInitialPage();
    },
    handleSearch() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = setTimeout(() => {
        this.currentPageIndex = 0;
        this.pageCount = 1;
        this.pages = [];
        this.searchAfter = null;
        this.selected = [];
        this.loadTrafficCount();
        this.loadInitialPage();
      }, 300);
    },
    async loadTrafficCount() {
      const params = this.getParams();
      await this.getTrafficCount(params);
    },
    async loadInitialPage() {
      const params = this.getParams();
      const pages = await this.fetchPage(params);
      if (pages) {
        this.pages = [pages];
        this.currentPageIndex = 0;
        this.pageCount = 1;
      }
    },
    async nextPage() {
      this.selected = [];
      if (this.currentPageIndex < this.pages.length - 1) {
        this.pageCount += this.pages[this.currentPageIndex].buckets.length;
        this.currentPageIndex += 1;
      } else if (this.hasMore) {
        const currentCursor = this.pages[this.currentPageIndex].afterKey;
        const params = this.getParams(currentCursor);
        const nextPageData = await this.fetchPage(params);
        if (nextPageData) {
          this.pages.push(nextPageData);
          this.pageCount += this.pages[this.currentPageIndex].buckets.length;
          this.currentPageIndex += 1;
        }
      }
    },
    onRowSelected(items) {
      this.selected = items;
    },
    previousPage() {
      this.selected = [];
      if (this.currentPageIndex > 0) {
        this.currentPageIndex -= 1;
        this.pageCount -= this.pageSize;
      }
    },
    collapseText,
    formatNumberWithCommas,
    hostnameFormatter,
  },
  async mounted() {
    await this.getPitId();
    await this.fetchThreatFeeds();
    await this.loadTrafficCount();
    await this.loadInitialPage();
  },
  watch: {
    selected() {
      if (this.selected.length === 0) return;
      const selectedRow = this.selected[0];
      this.$nextTick(() => {
        XomesEventBus.$emit('update-selected-domain', selectedRow.domain);
      });
    },
    timeRange() {
      this.selected = [];
      this.loadTrafficCount();
      this.loadInitialPage();
    },
  },
};
</script>

<style scoped>
.no-click {
  pointer-events: none;
}

.form-select{
  min-width: 160px;
  width: 20%;
}

.table-container {
  position: relative;
}

.nav-btns {
  display: flex;
  justify-content: end;
  padding-right: 1.5rem;
  padding-bottom: 1.0rem;
  gap: 0.5rem;   /* spacing between the two buttons */
}

.nav-btn {
  background-color: #2c2e37;  /* dark background */
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 0;     /* remove extra button padding */
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
}
.nav-btn:hover {
  cursor: not-allowed;
}
.nav-btn.active {
  opacity: unset;
}
.nav-btn.active:hover {
  cursor: pointer;
}
</style>
